import {
    colors,
    getMapSpecificityLevel,
    InsightConfig,
    Jurisdiction,
    MapAreaLevel,
    MapInsight,
    websiteURL,
} from '@hazadapt-git/public-core-base'
import { makeStyles } from 'tss-react/mui'
import { theme } from '../../../lib/styles/universal'
import {
    FC,
    Fragment,
    ReactNode,
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { BBox } from 'geojson'
import { MapHeader } from './MapHeader'
import { MapControls } from './MapControls'
import classNames from 'classnames'
import {
    AttributionControl,
    Layer,
    FillLayerSpecification,
    LineLayerSpecification,
    Map,
    MapRef,
    NavigationControl,
    Source,
    ViewState,
    ViewStateChangeEvent,
    MapWheelEvent,
    MapEvent,
    MapMouseEvent,
    MapTouchEvent,
} from 'react-map-gl/mapbox'
import mapboxgl, { Popup } from 'mapbox-gl'
import { renderToStaticMarkup } from 'react-dom/server'
import { LoadingOverlay, Markdown } from '../../atoms'
import {
    fillConfigurator,
    interactiveMapLayers,
    layerConfigurator,
    useWindowSizeUp,
} from '../../../lib/utils'
import { getYear } from 'date-fns'
import bboxPolygon, { bbox } from '@turf/bbox'
import { featureCollection } from '@turf/helpers'

export interface MapInsightViewProps extends Omit<MapInsight, 'headline'> {
    query_id?: string
    boundary?: Jurisdiction
    headline: ReactNode
    defaultZoom?: number
    minZoom: number
    defaultCenter?: number[]
    defaultSelectedAreas?: string[]
    onUpdate(
        placement_id: string,
        bbox: BBox,
        zoom: number,
        visibleTiles: string[],
        selectedAreas?: Set<string>,
        requery?: boolean
    ): Promise<void>
    className?: string
    config: InsightConfig
    onSwitchVariant: (variant: string) => void | Promise<void>
    onSwitchColorTheme: (theme: string) => void
    onSwitchMode: (mode: string) => void | Promise<void>
}

export const MapInsightView: FC<MapInsightViewProps> = ({
    defaultZoom,
    minZoom,
    defaultCenter,
    defaultSelectedAreas = [],
    onSwitchColorTheme,
    onUpdate,
    className,
    config,
    onSwitchVariant,
    placement_id,
    onSwitchMode,
    boundary,
    bbox: bounds,
    ...data
}) => {
    // MISC
    const { classes: localClasses } = useLocalStyles()
    const { variant, theme, mode } = config.pickers
    const lgScreens = useWindowSizeUp('lg')
    const { classes: popupClasses } = usePopupClasses()

    // REFS
    const defaultZoomRef = useRef(defaultZoom)
    const defaultCenterRef = useRef(defaultCenter)
    const mapContainerRef = useRef<HTMLDivElement>(null)
    const mapRef = useRef<MapRef>(null)
    const selectedTilesRef = useRef<Set<string>>(new Set(defaultSelectedAreas))
    const skipPostQueryRezoom = useRef(false)
    // Create a popup, but don't add it to the map yet.
    const popup = useRef<{
        elem: Popup
        feature_id: string | null
    }>({
        elem: new Popup({
            closeButton: false,
            closeOnClick: false,
        }),
        feature_id: null,
    })

    // STATES
    const [loading, setLoading] = useState<boolean>(false)
    const [viewState, setViewState] = useState<Partial<ViewState>>({})
    const [showOverlay, setShowOverlay] = useState<boolean>(false)
    const [mapExpanded, setMapExpanded] = useState<boolean>(false)
    const [activeTheme, setActiveTheme] = useState<string>(
        theme.selected_option ?? colors.insights.GREEN
    )
    const [activeVariant, setActiveVariant] = useState<string>(
        variant?.selected_option ?? ''
    )

    const visibleMapLayers = useMemo(() => {
        if (selectedTilesRef.current.size === 0) return interactiveMapLayers
        return interactiveMapLayers.filter((layer) =>
            data.map_config.some((tile) =>
                layer.startsWith(tile.id.split('-')[0])
            )
        )
    }, [data.map_config])

    const handleVariantChange = useCallback(
        async (selectedVariant: string) => {
            if (selectedVariant === activeVariant) return
            setActiveVariant(selectedVariant)
            setLoading(true)
            try {
                await onSwitchVariant(selectedVariant)
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        },
        [activeVariant, onSwitchVariant]
    )

    const handleModeChange = useCallback(
        async (selectedMode: string) => {
            if (selectedMode === mode.selected_option) return
            setLoading(true)
            try {
                await onSwitchMode(selectedMode)
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        },
        [onSwitchMode, mode?.selected_option]
    )

    const configurePopup = useCallback(
        (e: MapMouseEvent | MapTouchEvent) => {
            if (
                !e.features ||
                e.features.length === 0 ||
                !['Polygon', 'MultiPolygon'].includes(
                    e.features?.[0].geometry.type
                )
            )
                return
            const layerInfo = data.map_config.find(
                (t) => t.id === e.features?.[0].id
            )
            if (!layerInfo) return
            // Change the cursor style as a UI indicator.
            e.target.getCanvas().style.cursor = 'pointer'

            const rawContent = `**${layerInfo.name}**\n\n${layerInfo.label}`
            const content = document.createElement('div')
            content.innerHTML = renderToStaticMarkup(
                <Markdown content={rawContent} styleOverrides={popupClasses} />
            )

            // Populate the popup and set its coordinates on the user's mouse location.
            popup.current.elem.remove()
            popup.current.elem
                .setLngLat(e.lngLat)
                .setDOMContent(content)
                .addTo(e.target)
            popup.current.feature_id = layerInfo.id
        },
        [data, popupClasses]
    )

    const handleMouseMove = useCallback(
        (e: MapMouseEvent) => {
            popup.current.elem.setLngLat(e.lngLat)

            if (
                !e.features ||
                e.features.length === 0 ||
                !['Polygon', 'MultiPolygon'].includes(
                    e.features?.[0].geometry.type
                )
            )
                return
            const layerInfo = data.map_config.find(
                (t) => t.id === e.features?.[0].id
            )
            if (!layerInfo) return
            if (popup.current.feature_id === layerInfo.id) return

            const rawContent = `**${layerInfo.name}**\n\n${layerInfo.label}`
            const content = document.createElement('div')
            content.innerHTML = renderToStaticMarkup(
                <Markdown content={rawContent} styleOverrides={popupClasses} />
            )
            popup.current.elem.setDOMContent(content)
            popup.current.feature_id = layerInfo.id

            if (!popup.current.elem.isOpen()) {
                // This would happen if the mouse directly enters a tile from outside of the map container
                popup.current.elem.setLngLat(e.lngLat).addTo(e.target)
            }
        },
        [data, popupClasses]
    )

    const clearPopup = useCallback((e: MapMouseEvent | MapTouchEvent) => {
        e.target.getCanvas().style.cursor = ''
        popup.current.elem.remove()
        popup.current.feature_id = null
    }, [])

    const handleWheel = useCallback((e: MapWheelEvent) => {
        let overlayTimeout: NodeJS.Timeout | undefined
        e.target.on('wheel', (e) => {
            if (e.originalEvent.ctrlKey) {
                setShowOverlay(false)
                e.target.scrollZoom.enable()
                clearTimeout(overlayTimeout)
            } else {
                e.preventDefault()
                setShowOverlay(true)
                clearTimeout(overlayTimeout)
                overlayTimeout = setTimeout(() => {
                    setShowOverlay(false)
                }, 250)
            }
        })
    }, [])

    const initializeMapView = useCallback(
        (map: mapboxgl.Map | MapRef | null) => {
            if (!map) return
            if (defaultCenter && defaultZoom) {
                const center: [number, number] = [
                    defaultCenter[0],
                    defaultCenter[1],
                ]
                // Fly to center and default zoom of jurisdiction, without triggering a requery on zoom end
                map.flyTo(
                    { center: center, zoom: defaultZoom },
                    { requery: false }
                )
            } else {
                return
            }
        },
        [defaultCenter, defaultZoom]
    )

    const fitMapToBounds = useCallback(
        (
            map: mapboxgl.Map | MapRef,
            bbox: BBox,
            requery: boolean,
            firstRender = false
        ) => {
            map.fitBounds(
                [
                    [bbox[0] - 0.25, bbox[1] - 0.25], // fit to bound with a little padding
                    [bbox[2] + 0.25, bbox[3] + 0.25], // fit to bound with a little padding
                ],
                undefined,
                { requery, firstRender }
            )
        },
        []
    )

    const handleMapLoad = useCallback(
        (e: MapEvent) => {
            // not sure this initial resize is 100% necessary, let's look into it when we do a performance audit
            e.target.resize()
            e.target.scrollZoom.disable()
            if (selectedTilesRef.current.size === 0) {
                initializeMapView(e.target)
                return
            }

            if (bounds) {
                if (!isFinite(bounds[0])) return
                fitMapToBounds(e.target, bounds, false, true)
            } else {
                const features = e.target.queryRenderedFeatures({
                    layers: visibleMapLayers,
                })
                const bounds: BBox = bbox(featureCollection(features))
                if (!isFinite(bounds[0])) return
                fitMapToBounds(e.target, bounds, false, true)
            }
        },
        [bounds, fitMapToBounds, initializeMapView, visibleMapLayers]
    )

    const updateMapContents = useCallback(
        async (
            map: mapboxgl.Map | MapRef,
            requery = false,
            zoom = map.getZoom()
        ) => {
            if (loading) return
            const bounds = map.getBounds()
            if (!bounds) return
            const sw = bounds.getSouthWest()
            const ne = bounds.getNorthEast()
            const bbox = [sw.lng, sw.lat, ne.lng, ne.lat] as BBox
            setLoading(true)
            popup.current.elem.remove()
            popup.current.feature_id = null
            const tiles = map
                .queryRenderedFeatures({
                    layers: visibleMapLayers,
                })
                .filter(
                    (f) =>
                        !!f.id &&
                        f.id.toString().startsWith(getMapSpecificityLevel(zoom))
                )
                .map((f) => f.id?.toString()) as string[]
            try {
                if (requery) skipPostQueryRezoom.current = false
                await onUpdate(
                    placement_id,
                    bbox,
                    zoom,
                    tiles,
                    selectedTilesRef.current,
                    requery
                )
            } catch (err) {
                console.error(err)
            } finally {
                setLoading(false)
            }
        },
        [loading, visibleMapLayers, onUpdate, placement_id]
    )

    const expandOrCollapseMap = useCallback(() => {
        if (!mapContainerRef.current || !mapRef.current) return
        setMapExpanded((ex) => !ex)
        setTimeout(() => {
            if (!mapRef.current) return
            mapRef.current.resize()
        }, 500)
    }, [])

    const handleTileClick = useCallback(
        async (e: MapMouseEvent | MapTouchEvent) => {
            if (!mapRef.current) return

            // Ensure features exist and are valid
            const features = e.features ?? []
            const firstFeature = features[0]

            if (
                !firstFeature ||
                !['Polygon', 'MultiPolygon'].includes(
                    firstFeature.geometry?.type
                )
            ) {
                if (selectedTilesRef.current.size === 0) {
                    // Clicking out when no tile is selected; do nothing
                    return
                }
                // Clicking out when a tile is selected
                selectedTilesRef.current.clear()
                // Safely check for tileName
                const tileName = firstFeature?.properties?.name as
                    | string
                    | undefined
                if (
                    tileName &&
                    Object.values(MapAreaLevel).some((l) =>
                        tileName.startsWith(l)
                    )
                ) {
                    selectedTilesRef.current.add(tileName)
                    const bbox = bboxPolygon(firstFeature)
                    fitMapToBounds(mapRef.current, bbox, true)
                } else {
                    updateMapContents(
                        mapRef.current,
                        true,
                        defaultZoom ?? mapRef.current.getZoom()
                    )
                }
            } else {
                // Handle clicking on a valid tile
                skipPostQueryRezoom.current = true
                const tileName = firstFeature?.properties?.name as
                    | string
                    | undefined
                if (!tileName || selectedTilesRef.current.has(tileName)) {
                    // Clicking on the tile that's already selected; do nothing
                    return
                }
                selectedTilesRef.current.clear()
                selectedTilesRef.current.add(tileName)
                const bbox = bboxPolygon(firstFeature)
                fitMapToBounds(mapRef.current, bbox, true)
            }
        },
        [updateMapContents, fitMapToBounds, defaultZoom]
    )

    const handleMapMovement = useCallback(
        async (
            e: ViewStateChangeEvent & {
                requery?: boolean
                firstRender?: boolean
            }
        ) => {
            // Type guard for originalEvent
            const hasOriginalEvent = (
                evt: typeof e
            ): evt is typeof e & { originalEvent: Event } =>
                'originalEvent' in evt && evt.originalEvent !== undefined

            if (
                !e.requery && // No requery
                (e.firstRender || // First render; don't requery
                    loading || // Requery already in progress
                    e.target.isMoving() || // Map is currently moving
                    !hasOriginalEvent(e) || // No map event; not triggered by explicit user action on map; don't requery through handleMapMovement
                    e.originalEvent.type === 'resize') // resize event; requery via handleMapResize instead of handleMapMovement
            ) {
                return
            }
            updateMapContents(e.target, e.requery)
        },
        [updateMapContents, loading]
    )

    const resizeTimer = useRef<NodeJS.Timeout>()
    const handleMapResize = useCallback(
        (e: MapEvent) => {
            if (loading || resizeTimer.current) return
            const resetTimer = () => {
                // Reset the timer
                resizeTimer.current = undefined
            }

            // Manual debouncing of update-on-resize
            resizeTimer.current = setTimeout(() => {
                if (!mapRef.current) {
                    resetTimer()
                    return
                }
                setLoading(true)
                updateMapContents(mapRef.current).finally(resetTimer)
            }, 250)
        },
        [updateMapContents, loading]
    )

    const updateViewState = useCallback((e: ViewStateChangeEvent) => {
        setViewState(e.viewState)
    }, [])

    const observeResize = useCallback(
        (
            container: HTMLDivElement,
            previousDimensions: { height: number; width: number }
        ) => {
            const resizeObserver = new ResizeObserver((entries) => {
                for (let entry of entries) {
                    const { height: newHeight, width: newWidth } =
                        entry.contentRect

                    const heightDiff = Math.abs(
                        newHeight - previousDimensions.height
                    )
                    const widthDiff = Math.abs(
                        newWidth - previousDimensions.width
                    )

                    const SIZE_THRESHOLD = 30

                    if (
                        heightDiff > SIZE_THRESHOLD ||
                        widthDiff > SIZE_THRESHOLD
                    ) {
                        previousDimensions.height = newHeight
                        previousDimensions.width = newWidth
                        if (mapRef.current) {
                            mapRef.current.resize()
                        }
                    }
                }
            })

            resizeObserver.observe(container)

            return () => {
                resizeObserver.unobserve(container)
            }
        },
        []
    )

    useEffect(() => {
        selectedTilesRef.current = new Set(defaultSelectedAreas)
    }, [defaultSelectedAreas])

    useEffect(() => {
        if (!bounds || (bounds.length !== 4 && bounds.length !== 6)) return
        if (selectedTilesRef.current.size === 0) {
            initializeMapView(mapRef.current)
            return
        }
        if (selectedTilesRef.current.size > 0 && skipPostQueryRezoom.current) {
            return
        }
        if (mapRef.current)
            fitMapToBounds(mapRef.current, bounds as BBox, false)
        skipPostQueryRezoom.current = false
        // This dep list is intentional; do NOT retrigger on every change of the bbox, only when the query ID is updated
        // (i.e. when the user clicks into or out of an area, not when the map is moved)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data.query_id, fitMapToBounds])

    useEffect(() => {
        const newViewState: Partial<ViewState> = {}
        if (defaultCenterRef.current) {
            newViewState.longitude = defaultCenterRef.current[0]
            newViewState.latitude = defaultCenterRef.current[1]
        }
        if (defaultZoomRef.current !== undefined) {
            newViewState.zoom = defaultZoomRef.current
        }
        const resetViewState = () =>
            setViewState((vs) => ({
                ...vs,
                ...newViewState,
            }))

        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.ctrlKey) {
                setShowOverlay(false)
            }
        }

        resetViewState()

        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('page-refresh', resetViewState)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('page-refresh', resetViewState)
        }
    }, [])

    useEffect(() => {
        if (!mapContainerRef.current) return

        const mapContainer = mapContainerRef.current

        const cleanupChildObserver = observeResize(mapContainer, {
            height: mapContainer.clientHeight,
            width: mapContainer.clientWidth,
        })
        return cleanupChildObserver
    }, [observeResize])

    const getLayers = useCallback(
        (level: MapAreaLevel) => {
            if (!Object.values(MapAreaLevel).includes(level)) {
                // Invalid level value, don't render any layers
                return []
            }

            if (
                selectedTilesRef.current.size > 0 &&
                !data.map_config.some((tile) => tile.id.split('-')[0] === level)
            ) {
                // If querying over a specific area, force only that layer to render
                return []
            }

            return (
                <Fragment key={level}>
                    <Layer
                        id={`${level}-fill`}
                        type="fill"
                        {...(layerConfigurator(
                            data.map_config,
                            level,
                            selectedTilesRef.current.size
                        ) as Partial<FillLayerSpecification>)}
                        paint={{
                            'fill-color': fillConfigurator(
                                data.map_config,
                                level,
                                activeTheme
                            ),
                        }}
                    />
                    <Layer
                        id={`${level}-line`}
                        type="line"
                        {...(layerConfigurator(
                            data.map_config,
                            level,
                            selectedTilesRef.current.size
                        ) as Partial<LineLayerSpecification>)}
                        paint={{
                            'line-color': colors.grays.NIMBUS,
                            'line-width': 1,
                        }}
                    />
                </Fragment>
            )
        },
        [activeTheme, data.map_config]
    )

    return (
        <>
            <MapHeader
                placement_id={placement_id}
                headline={data.headline}
                explainer={data.explainer}
                handleModeChange={handleModeChange}
                mode={mode}
                mapExpanded={mapExpanded}
                expandOrCollapseMap={expandOrCollapseMap}
            />

            <div
                className={classNames(localClasses.mapContainer, className, {
                    [localClasses.expandedMapContainer]: mapExpanded,
                })}
                ref={mapContainerRef}
            >
                <MapControls
                    themeConfig={theme}
                    variantConfig={variant}
                    activeVariant={activeVariant}
                    activeTheme={activeTheme}
                    setActiveTheme={setActiveTheme}
                    handleThemeChange={onSwitchColorTheme}
                    handleVariantChange={handleVariantChange}
                />

                <Map
                    {...viewState}
                    ref={mapRef}
                    mapLib={import('mapbox-gl')}
                    projection={{ name: 'mercator' }}
                    mapStyle="mapbox://styles/mapbox/light-v11"
                    style={{ height: '100%' }}
                    onLoad={handleMapLoad}
                    onMove={updateViewState}
                    interactiveLayerIds={interactiveMapLayers}
                    minZoom={minZoom}
                    onClick={handleTileClick}
                    onMoveEnd={handleMapMovement}
                    onResize={handleMapResize}
                    reuseMaps
                    onMouseEnter={configurePopup}
                    onMouseMove={handleMouseMove}
                    onMouseLeave={clearPopup}
                    onMouseOut={clearPopup}
                    onTouchEnd={configurePopup}
                    onTouchCancel={clearPopup}
                    onWheel={handleWheel}
                    touchPitch={false}
                    pitchWithRotate={false}
                    attributionControl={false}
                    pitch={0}
                >
                    <NavigationControl
                        style={{
                            marginTop:
                                lgScreens ||
                                !variant ||
                                variant.options.length === 0
                                    ? '0.5625rem'
                                    : '3.75rem',
                        }}
                    />
                    <AttributionControl
                        position="bottom-right"
                        customAttribution={`&copy; ${getYear(
                            Date.now()
                        )} <a target="_blank" href=${websiteURL}>HazAdapt, Inc.</a>`}
                    />

                    <Source
                        id="states"
                        type="vector"
                        url={`mapbox://${
                            process.env.REACT_APP_MAPBOX_STATES_TILESET_ID ?? ''
                        }`}
                        promoteId="id"
                    >
                        {getLayers(MapAreaLevel.STATE)}
                    </Source>
                    <Source
                        id="counties"
                        type="vector"
                        url={`mapbox://${
                            process.env.REACT_APP_MAPBOX_COUNTIES_TILESET_ID ??
                            ''
                        }`}
                        promoteId="id"
                    >
                        {getLayers(MapAreaLevel.COUNTY)}
                    </Source>
                    <Source
                        id="zips"
                        type="vector"
                        url={`mapbox://${
                            process.env.REACT_APP_MAPBOX_ZIPS_TILESET_ID ?? ''
                        }`}
                        promoteId="id"
                    >
                        {getLayers(MapAreaLevel.ZIP)}
                    </Source>
                </Map>
            </div>

            {/* LOADING AND HOTKEY OVERLAYS */}
            {loading || showOverlay ? (
                <LoadingOverlay loading={loading} showOverlay={showOverlay} />
            ) : null}
        </>
    )
}

const useLocalStyles = makeStyles()({
    mapContainer: {
        height: '15rem',
        position: 'relative',
        transition: 'all .3s ease-in-out',
        [theme.breakpoints.up('md')]: {
            height: '20rem',
        },
        [theme.breakpoints.up('xl')]: {
            height: '25rem',
        },
    },
    expandedMapContainer: {
        height: '30rem',
        [theme.breakpoints.up('md')]: {
            height: '40rem',
        },
        [theme.breakpoints.up('xl')]: {
            height: '50rem',
        },
    },
})

const usePopupClasses = makeStyles()({
    ul: {
        paddingLeft: '1rem',
        '& p': {
            fontSize: '0.875rem',
            lineHeight: '1.125rem',
        },
    },
    ol: {
        paddingLeft: '1rem',
        '& p': {
            fontSize: '0.875rem',
            lineHeight: '1.125rem',
        },
    },
})
